import {
  Button,
  Card,
  Center,
  Flex,
  Group,
  Pagination,
  Stack,
  Text
} from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useGetDeviceList,
  useGetTrackingVersion
} from '@/queries/deviceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { DeviceCardSkeleton } from '../ui-shared/DeviceCardSkeleton/DeviceCardSkeleton'
import { ErrorWithReload } from '../ui-shared/ErrorWithReload/ErrorWithReload'
import { PlatformAlert } from '../ui-shared/PlatformAlert/PlatformAlert'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { CrossCameraDeviceCard } from './CrossCameraDeviceCard/CrossCameraDeviceCard'

const PAGE_SIZE = 5
const SKELETON_COUNT = 3
const POLL_INTERVAL = 10000

export const CrossCameraInstallationPage = () => {
  const [canManageDevices] = useHasPermissions(Resource.Devices, [
    CrudAction.Create
  ])
  const [tableState, setTableState] = useTableState({ activePage: 1 })
  const { activePage } = tableState

  const { data, isLoading, isFetching, isError, refetch } = useGetDeviceList(
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE,
      ordering: 'name'
    },
    { keepPreviousData: true, refetchInterval: POLL_INTERVAL }
  )
  const { data: trackingVersionData } = useGetTrackingVersion({
    refetchInterval: POLL_INTERVAL
  })
  const latestVersion = trackingVersionData?.trim()

  const devices = data?.results ?? []
  const totalPageCount = getPageCount(data?.count ?? 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handlePageChange = (value: number) => {
    setTableState({ activePage: value })
  }

  const renderContent = () => {
    if (!isLoading && devices.length === 0) {
      return (
        <Card withBorder>
          <Center h={160}>
            <Text c="dimmed">
              <FormattedMessage id="crossCamera.installation.empty" />
            </Text>
          </Center>
        </Card>
      )
    }

    return (
      <Stack>
        {isLoading
          ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <DeviceCardSkeleton key={i} />
            ))
          : devices.map((device) => (
              <CrossCameraDeviceCard
                key={device.id}
                device={device}
                canManage={canManageDevices}
                latestVersion={latestVersion}
              />
            ))}
      </Stack>
    )
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={() => void refetch()}>
        <FormattedMessage id="devices.data.error" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <PlatformAlert
        variant="warning"
        title={
          <FormattedMessage id="crossCamera.installation.requirements.title" />
        }
        mb="lg"
      >
        <FormattedMessage id="crossCamera.installation.requirements" />
      </PlatformAlert>

      <Group justify="end" mb="xs">
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={isFetching}
          onClick={() => void refetch()}
        >
          <FormattedMessage id="refresh" />
        </Button>
      </Group>

      {renderContent()}

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={handlePageChange}
        />
      </Flex>
    </>
  )
}
